import { api, type Lilypad, type Media } from '../api';
import { h, errMsg, type Session } from './panels';

type Phase = 'checking' | 'ready' | 'blocked' | 'waiting' | 'uploading' | 'received' | 'preparing' | 'added' | 'failed' | 'cancelled';
type Kind = 'photo' | 'video';
interface Inspection { mime: string; kind: Kind | null; error: string | null; heic: boolean }
interface Limits { maxBytes: number; photoMime?: readonly string[]; videoMime?: readonly string[] }
const PHOTOS = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/gif'];
const VIDEOS = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-matroska', 'video/3gpp'];
const EXTENSIONS: Record<string, string> = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', heic: 'image/heic', heif: 'image/heif', gif: 'image/gif', mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm', mkv: 'video/x-matroska', '3gp': 'video/3gpp', '3gpp': 'video/3gpp' };
const bytes = (size: number): string => size >= 1024 ** 3 ? `${(size / 1024 ** 3).toFixed(1)} GB` : size >= 1024 ** 2 ? `${(size / 1024 ** 2).toFixed(1)} MB` : `${Math.max(1, Math.round(size / 1024))} KB`;
const minutes = (seconds: number): string => `${Math.round(seconds / 60)} minutes`;

/** Preflight is a convenience; the server still checks the actual bytes and current entitlement. */
export function inspectUploadFile(file: Pick<File, 'name' | 'type' | 'size'>, limits: Limits): Inspection {
  const declared = file.type.toLowerCase();
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const mime = !declared || declared === 'application/octet-stream' ? EXTENSIONS[extension] ?? declared : declared === 'image/jpg' ? 'image/jpeg' : declared;
  const photos = limits.photoMime ?? PHOTOS, videos = limits.videoMime ?? VIDEOS;
  const kind: Kind | null = photos.includes(mime) ? 'photo' : videos.includes(mime) ? 'video' : null;
  let error: string | null = null;
  if (!Number.isFinite(file.size) || file.size <= 0) error = 'This file is empty. Choose another copy.';
  else if (file.size > limits.maxBytes) error = `This file is ${bytes(file.size)}. The limit is ${bytes(limits.maxBytes)} per file.`;
  else if (!kind) error = 'This file type is not supported. Choose a supported photo or video.';
  return { mime, kind, error, heic: mime === 'image/heic' || mime === 'image/heif' };
}

/** A received original is never retransferred merely because its display copy is still being prepared. */
export function needsUpload(phase: Phase, hasReceipt: boolean): boolean {
  return !hasReceipt && (phase === 'ready' || phase === 'failed' || phase === 'cancelled');
}

function clientId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const values = crypto.getRandomValues(new Uint8Array(16));
  values[6] = (values[6] & 15) | 64; values[8] = (values[8] & 63) | 128;
  const value = [...values].map((n) => n.toString(16).padStart(2, '0')).join('');
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
}

/** A broken/unsupported local decoder must not hold a whole selection indefinitely. */
function durationOf(file: File, signal: AbortSignal): Promise<number | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    let finished = false;
    const finish = (duration: number | null) => {
      if (finished) return;
      finished = true; clearTimeout(timeout); signal.removeEventListener('abort', aborted);
      video.onloadedmetadata = null; video.onerror = null;
      video.removeAttribute('src'); video.load(); URL.revokeObjectURL(url);
      resolve(duration);
    };
    const aborted = () => finish(null);
    const timeout = window.setTimeout(() => finish(null), 8000);
    video.preload = 'metadata';
    video.onloadedmetadata = () => finish(Number.isFinite(video.duration) ? video.duration : null);
    video.onerror = () => finish(null);
    signal.addEventListener('abort', aborted, { once: true });
    if (signal.aborted) finish(null); else video.src = url;
  });
}

interface Selection {
  id: string;
  file: File;
  inspection: Inspection;
  caption: string;
  sentCaption: string | null;
  phase: Phase;
  error: string;
  duration: number | null;
  progress: number;
  attempted: boolean;
  receiptUnknown: boolean;
  media: Media | null;
  announced: boolean;
  lastChecked: number;
  request: AbortController | null;
  check: AbortController | null;
  previewUrl: string | null;
  previewMedia: HTMLImageElement | HTMLVideoElement | null;
  row: HTMLLIElement;
  preview: HTMLDivElement;
  status: HTMLParagraphElement;
  detail: HTMLParagraphElement;
  captionInput: HTMLInputElement;
  progressElement: HTMLProgressElement;
  retry: HTMLButtonElement;
  cancel: HTMLButtonElement;
  remove: HTMLButtonElement;
  checkReceipt: HTMLButtonElement;
}

/** Real staged uploads, bound to one immutable event destination for the lifetime of this panel. */
export function uploadFlow(s: Session, lp: Lilypad, onAdded: () => void, onDone?: () => void): HTMLElement {
  const eventId = lp.id, eventTitle = lp.title;
  const componentId = `upload-${clientId()}`;
  const title = h('h2', { id: `${componentId}-title`, tabIndex: -1 }, 'Add photos & videos');
  const panel = h('section', { class: 'upload-flow', 'data-wide': '1', 'aria-labelledby': title.id });
  const selections: Selection[] = [];
  let disposed = false, busy = false, activeChecks = 0, poll: number | undefined, pollRounds = 0;
  const policy = lp as Lilypad & { can_contribute?: boolean; canContribute?: boolean };
  const allowed = () => { const user = s.user; return !!user?.email_verified_at && !!user.phone_verified_at && !!s.ent?.canUpload && policy.can_contribute !== false && policy.canContribute !== false; };
  const videoCap = () => s.ent?.maxVideoSeconds ?? s.rules.videoMaxSecondsFree;
  const limits = (): Limits => { const rules = s.rules as typeof s.rules & { photoMime?: string[]; videoMime?: string[] }; return { maxBytes: rules.uploadMaxBytes, photoMime: rules.photoMime, videoMime: rules.videoMime }; };
  const permission = h('p', { class: 'notice warn', hidden: true });
  const statusLive = h('p', { class: 'upload-flow__summary notice', role: 'status', 'aria-live': 'polite', 'aria-atomic': 'true' }, 'Choose photos and videos to review before uploading.');
  const list = h('ul', { class: 'upload-flow__items', 'aria-label': 'Selected files' });
  const fileInput = h('input', { type: 'file', id: `${componentId}-files`, accept: [...(limits().photoMime ?? PHOTOS), ...(limits().videoMime ?? VIDEOS)].join(','), multiple: true, hidden: true });
  const choose = h('button', { type: 'button', class: 'btn secondary', onClick: () => fileInput.click() }, 'Choose photos & videos');
  const upload = h('button', { type: 'button', class: 'btn', disabled: true, onClick: () => void run(selections.filter((item) => item.phase === 'ready')) }, 'Upload');
  const retryAll = h('button', { type: 'button', class: 'btn secondary', hidden: true, onClick: () => void run(selections.filter((item) => item.phase === 'failed' && !item.media)) }, 'Retry failed files');
  const cancelRemaining = h('button', { type: 'button', class: 'btn secondary', hidden: true, onClick: () => { selections.filter((item) => item.phase === 'waiting' || item.phase === 'uploading').forEach(cancelItem); updateSummary(); } }, 'Stop remaining uploads');
  const done = h('button', { type: 'button', class: 'btn secondary', hidden: !onDone, onClick: () => onDone?.() }, 'Back to event');
  const drop = h('div', { class: 'upload-flow__drop', role: 'group', 'aria-label': 'Choose or drop files',
    onDragover: (event: DragEvent) => { event.preventDefault(); if (allowed() && !busy) drop.classList.add('active'); },
    onDragleave: () => drop.classList.remove('active'),
    onDrop: (event: DragEvent) => { event.preventDefault(); drop.classList.remove('active'); if (event.dataTransfer?.files) addFiles(Array.from(event.dataTransfer.files)); },
  }, h('div', {}, h('h3', {}, 'Choose memories to share'), h('p', { class: 'muted' }, 'Choose files, or drag them here. Review them before uploading.')), choose);
  const date = new Date(lp.event_date.length === 10 ? `${lp.event_date}T12:00:00` : lp.event_date);
  panel.append(title, h('p', { class: 'muted' }, 'Review your selection and add a few words if you like.'),
    h('div', { class: 'upload-flow__destination' }, h('div', {}, h('p', { class: 'muted' }, 'Adding to'), h('strong', {}, eventTitle), h('p', { class: 'muted' }, `${Number.isNaN(date.valueOf()) ? lp.event_date : date.toLocaleDateString()} · ${lp.owner_name} · Members only`))),
    permission, h('p', { class: 'notice' }, `Your video limit: ${minutes(videoCap())}. File-size limit: ${bytes(s.rules.uploadMaxBytes)} per file. Originals stay on your device.`),
    drop, fileInput, list, statusLive,
    h('div', { class: 'upload-flow__footer row' }, upload, retryAll, cancelRemaining, done),
    h('p', { class: 'muted' }, 'Keep this page open during upload. Leaving stops transfers on this device; received files remain in the event. After a refresh you may need to choose files again.'));

  fileInput.addEventListener('change', () => { if (fileInput.files) addFiles(Array.from(fileInput.files)); fileInput.value = ''; });
  function message(item: Selection): string {
    const text: Record<Phase, string> = { checking: 'Checking this file…', ready: 'Ready to upload', blocked: 'Not included', waiting: 'Waiting to upload', uploading: item.progress >= 1 ? 'Upload sent · Waiting for confirmation…' : `Uploading · ${Math.round(item.progress * 100)}%`, received: 'Upload received · Display copy needs attention', preparing: 'Upload received · Preparing video…', added: 'Added', failed: 'Upload not confirmed', cancelled: item.attempted ? 'Stopped on this device' : 'Cancelled · Not uploaded' };
    return text[item.phase];
  }
  function updateItem(item: Selection): void {
    item.row.dataset.state = item.phase;
    item.status.textContent = message(item);
    item.status.dataset.state = item.phase;
    item.detail.textContent = item.error || (item.phase === 'ready' && item.inspection.kind === 'video' && item.duration == null ? 'Your browser could not read its duration. The server will check this video after upload.' : item.duration != null ? `Video length: ${Math.round(item.duration)} seconds` : item.inspection.heic ? 'HEIC preview is available after upload. The original file will be preserved.' : '');
    item.progressElement.hidden = item.phase !== 'uploading';
    item.progressElement.value = item.progress;
    item.progressElement.setAttribute('aria-valuetext', item.progress >= 1 ? 'Sent; waiting for server confirmation' : `${Math.round(item.progress * 100)} percent`);
    item.captionInput.disabled = item.attempted || item.phase === 'blocked';
    item.retry.hidden = !needsUpload(item.phase, !!item.media) || item.phase === 'ready';
    item.retry.disabled = busy || !allowed();
    item.cancel.hidden = !['checking', 'waiting', 'uploading'].includes(item.phase);
    item.remove.hidden = ['waiting', 'uploading', 'received', 'preparing', 'added'].includes(item.phase);
    item.checkReceipt.hidden = !item.attempted || ['waiting', 'uploading'].includes(item.phase) || !(item.receiptUnknown || item.phase === 'preparing' || item.phase === 'received');
    item.checkReceipt.disabled = item.request != null;
  }
  function updateSummary(): void {
    if (disposed) return;
    panel.dataset.busy = String(busy);
    const count = (phase: Phase) => selections.filter((item) => item.phase === phase).length;
    const confirmed = selections.filter((item) => item.media != null).length;
    const checking = count('checking'), ready = count('ready'), failed = count('failed'), preparing = count('preparing');
    const parts = [count('added') ? `${count('added')} added` : '', preparing ? `${preparing} preparing` : '', count('received') ? `${count('received')} received; display needs attention` : '', count('uploading') ? '1 uploading' : '', count('waiting') ? `${count('waiting')} waiting` : '', checking ? `${checking} checking` : '', ready ? `${ready} ready` : '', failed ? `${failed} not confirmed` : '', count('blocked') ? `${count('blocked')} not included` : '', count('cancelled') ? `${count('cancelled')} stopped or cancelled` : ''].filter(Boolean);
    statusLive.textContent = parts.length ? `${eventTitle}: ${parts.join(' · ')}.` : 'Choose photos and videos to review before uploading.';
    upload.textContent = busy ? 'Uploading…' : checking ? 'Checking selection…' : `Upload ${ready} ${ready === 1 ? 'file' : 'files'}`;
    upload.disabled = busy || checking > 0 || ready === 0 || !allowed();
    upload.hidden = ready === 0 && !busy && checking === 0;
    retryAll.hidden = failed === 0;
    retryAll.disabled = busy || !allowed();
    choose.disabled = busy || !allowed(); fileInput.disabled = choose.disabled;
    cancelRemaining.hidden = !busy;
    done.disabled = busy;
    done.textContent = confirmed ? 'Open event' : 'Back to event';
    permission.hidden = allowed();
    permission.textContent = policy.can_contribute === false || policy.canContribute === false ? 'This event is view-only for you. You can browse its photos, but cannot add files.' : 'Uploading is unavailable for this account right now. You can still return to the event.';
    selections.forEach(updateItem);
  }
  function placeholder(item: Selection, text: string): void {
    delete item.preview.dataset.media;
    item.preview.replaceChildren(h('span', { class: 'muted' }, text));
  }
  function releasePreview(item: Selection): void {
    if (item.previewMedia instanceof HTMLVideoElement) { item.previewMedia.pause(); item.previewMedia.removeAttribute('src'); item.previewMedia.load(); }
    item.previewMedia = null;
    if (item.previewUrl) { URL.revokeObjectURL(item.previewUrl); item.previewUrl = null; }
  }
  function makePreview(item: Selection): void {
    if (item.inspection.heic) { placeholder(item, 'HEIC photo'); return; }
    if (!item.inspection.kind || item.inspection.error) { placeholder(item, item.inspection.kind === 'video' ? 'Video' : 'File'); return; }
    item.previewUrl = URL.createObjectURL(item.file);
    if (item.inspection.kind === 'photo') {
      const image = h('img', { src: item.previewUrl, alt: `Preview of ${item.file.name}`, loading: 'lazy' });
      image.addEventListener('error', () => { if (disposed || item.previewMedia !== image) return; placeholder(item, 'Photo preview unavailable'); releasePreview(item); }, { once: true });
      item.previewMedia = image; item.preview.replaceChildren(image);
    } else {
      const video = h('video', { src: item.previewUrl, preload: 'metadata', muted: true, playsInline: true, 'aria-label': `Video preview of ${item.file.name}` });
      video.addEventListener('loadedmetadata', () => { try { if (Number.isFinite(video.duration) && video.duration > 0.1) video.currentTime = Math.min(0.2, video.duration / 2); } catch { /* A labeled video tile remains useful without a decoded frame. */ } }, { once: true });
      video.addEventListener('error', () => { if (disposed || item.previewMedia !== video) return; placeholder(item, 'Video preview unavailable'); releasePreview(item); }, { once: true });
      item.previewMedia = video; item.preview.replaceChildren(video);
    }
  }
  function makeItem(file: File): Selection {
    const id = clientId(), inspection = inspectUploadFile(file, limits());
    const preview = h('div', { class: 'upload-flow__preview' });
    const status = h('p', { class: 'upload-flow__status', tabIndex: -1 });
    const detail = h('p', { class: 'muted', id: `${componentId}-${id}-detail` });
    const captionInput = h('input', { id: `${componentId}-${id}-caption`, maxLength: 1000, placeholder: 'Add a few words…', 'aria-describedby': detail.id });
    const progressElement = h('progress', { class: 'upload-flow__progress', max: 1, value: 0, hidden: true, 'aria-label': `Uploading ${file.name}` });
    const retry = h('button', { class: 'btn small secondary', type: 'button', hidden: true }, 'Retry this file');
    const cancel = h('button', { class: 'btn small secondary', type: 'button', hidden: true }, 'Stop upload');
    const remove = h('button', { class: 'btn small secondary', type: 'button', 'aria-label': `Remove ${file.name} from this selection` }, 'Remove');
    const checkReceipt = h('button', { class: 'btn small secondary', type: 'button', hidden: true }, 'Check status');
    const row = h('li', { class: 'upload-flow__item' }, preview,
      h('div', { class: 'upload-flow__detail' }, h('strong', {}, file.name), h('p', { class: 'muted' }, bytes(file.size)), status, detail, progressElement, h('label', { for: captionInput.id }, 'Caption (optional)'), captionInput),
      h('div', { class: 'upload-flow__actions row' }, retry, cancel, remove, checkReceipt));
    const item: Selection = { id, file, inspection, caption: '', sentCaption: null, phase: inspection.error ? 'blocked' : inspection.kind === 'video' ? 'checking' : 'ready', error: inspection.error ?? '', duration: null, progress: 0, attempted: false, receiptUnknown: false, media: null, announced: false, lastChecked: 0, request: null, check: null, previewUrl: null, previewMedia: null, row, preview, status, detail, captionInput, progressElement, retry, cancel, remove, checkReceipt };
    captionInput.addEventListener('input', () => { item.caption = captionInput.value; });
    retry.addEventListener('click', () => { item.status.focus(); void run([item]); });
    cancel.addEventListener('click', () => { item.status.focus(); cancelItem(item); updateSummary(); });
    remove.addEventListener('click', () => {
      if (item.request || item.media) return;
      item.check?.abort(); releasePreview(item);
      const index = selections.indexOf(item); if (index >= 0) selections.splice(index, 1);
      row.remove(); updateSummary(); choose.focus();
    });
    checkReceipt.addEventListener('click', async () => { item.checkReceipt.disabled = true; await reconcile(item, true); if (!disposed) { updateSummary(); item.checkReceipt.disabled = false; } });
    makePreview(item);
    return item;
  }
  function addFiles(files: File[]): void {
    if (disposed || busy || !allowed()) return;
    for (const file of files) { const item = makeItem(file); selections.push(item); list.append(item.row); }
    updateSummary(); pumpChecks();
  }
  function pumpChecks(): void {
    if (disposed) return;
    for (const item of selections) {
      if (activeChecks >= 2) break;
      if (item.phase !== 'checking' || item.check) continue;
      item.check = new AbortController(); activeChecks++;
      void durationOf(item.file, item.check.signal).then((duration) => {
        if (disposed || item.phase !== 'checking') return;
        item.duration = duration;
        if (duration != null && duration > videoCap() + 1) { item.phase = 'blocked'; item.error = `This video is ${Math.round(duration)} seconds. Your current limit is ${minutes(videoCap())}. Trim it on your device, then choose the shorter copy.`; }
        else item.phase = 'ready';
      }).catch(() => { if (item.phase === 'checking') item.phase = 'ready'; }).finally(() => { item.check = null; activeChecks--; if (!disposed) { updateSummary(); pumpChecks(); } });
    }
  }
  function cancelItem(item: Selection): void {
    if (!['checking', 'waiting', 'uploading'].includes(item.phase)) return;
    item.check?.abort(); item.request?.abort();
    item.phase = 'cancelled';
    item.receiptUnknown = item.attempted;
    item.error = item.attempted ? 'Stopped on this device. The server may still finish receiving it; Check status or retry this same file to reconcile it safely.' : 'Not uploaded. The original is still on your device.';
    updateItem(item);
    if (item.attempted) schedulePoll();
  }
  function received(item: Selection, media: Media): void {
    item.media = media; item.receiptUnknown = false; item.progress = 1;
    if (media.rendition_state === 'pending') { item.phase = 'preparing'; item.error = 'Received by the event. You can continue browsing while the display copy is prepared.'; }
    else if (media.rendition_state === 'failed') { item.phase = 'received'; item.error = 'The original was received, but its display copy could not be prepared. Check status later; retrying the transfer will not fix processing.'; }
    else { item.phase = 'added'; item.error = ''; }
    if (media.has_thumb && item.preview.dataset.media !== media.id) {
      releasePreview(item);
      const image = h('img', { src: api.thumbUrl(media.id), loading: 'lazy', alt: `Thumbnail of ${item.file.name}` });
      image.addEventListener('error', () => { if (!disposed && item.previewMedia === image) placeholder(item, 'Received · Preview unavailable'); }, { once: true });
      item.previewMedia = image; item.preview.replaceChildren(image); item.preview.dataset.media = media.id;
    }
    if (!item.announced) { item.announced = true; if (!disposed) { try { onAdded(); } catch { /* The accepted receipt remains valid if a surrounding view refresh fails. */ } } }
  }
  async function reconcile(item: Selection, explicit = false): Promise<boolean> {
    if (disposed || !selections.includes(item)) return false;
    item.lastChecked = Date.now();
    try {
      const receipt = await api.uploadReceipt(eventId, item.id);
      if (disposed || !selections.includes(item)) return false;
      if (receipt.media) { received(item, receipt.media); return true; }
      if (explicit && !item.media) item.error = 'No completed receipt yet. The server may still be receiving this file. Retry uses the same upload identity to avoid adding it twice.';
    } catch (error) {
      if (explicit && !disposed) item.error = `Could not check status: ${errMsg(error)}. Your existing upload identity will be reused if you retry.`;
    }
    return false;
  }
  function schedulePoll(): void {
    if (disposed || poll != null || pollRounds >= 18 || !selections.some((item) => item.receiptUnknown || item.phase === 'preparing')) return;
    poll = window.setTimeout(async () => {
      poll = undefined;
      if (disposed) return;
      pollRounds++;
      const pending = selections.filter((item) => !item.request && !['waiting', 'uploading'].includes(item.phase) && (item.receiptUnknown || item.phase === 'preparing')).sort((a, b) => a.lastChecked - b.lastChecked).slice(0, 3);
      await Promise.allSettled(pending.map((item) => reconcile(item)));
      updateSummary(); schedulePoll();
    }, 10_000);
  }
  async function run(requested: Selection[]): Promise<void> {
    if (disposed || busy || !allowed()) return;
    const queue = requested.filter((item) => selections.includes(item) && needsUpload(item.phase, !!item.media));
    if (!queue.length) return;
    busy = true; pollRounds = 0;
    for (const item of queue) { item.phase = 'waiting'; item.error = ''; }
    updateSummary();
    try {
      for (const item of queue) {
        if (disposed) break;
        if (item.phase !== 'waiting' || !selections.includes(item)) continue;
        if (!allowed()) { item.phase = 'failed'; item.error = 'Your current account or event access no longer allows uploading.'; continue; }
        if (item.attempted && await reconcile(item)) { updateSummary(); continue; }
        if (disposed || item.phase !== 'waiting') continue;
        const check = inspectUploadFile(item.file, limits());
        if (check.error || (item.duration != null && item.duration > videoCap() + 1)) { item.phase = 'blocked'; item.error = check.error ?? `Your video limit is now ${minutes(videoCap())}. Choose a shorter clip.`; continue; }
        item.sentCaption ??= item.caption;
        item.attempted = true; item.receiptUnknown = true; item.phase = 'uploading'; item.progress = 0;
        const request = new AbortController(); item.request = request;
        updateSummary();
        try {
          // A MIME-less HEIC/MOV file keeps its bytes; only the multipart type is normalized for the server.
          const file = item.file.type === check.mime ? item.file : new File([item.file], item.file.name, { type: check.mime, lastModified: item.file.lastModified });
          const result = await api.upload(eventId, file, item.sentCaption, (progress) => { if (!disposed && item.phase === 'uploading') { item.progress = Math.max(0, Math.min(1, progress)); updateItem(item); } }, { clientUploadId: item.id, signal: request.signal });
          if (!disposed) received(item, result.media);
        } catch (error) {
          if (disposed) break;
          item.phase = request.signal.aborted ? 'cancelled' : 'failed';
          item.error = request.signal.aborted ? 'Stopped on this device. The server may still finish receiving it. Check status before choosing another copy.' : `${errMsg(error)}. Receipt not confirmed; retry will reuse this file's upload identity.`;
        } finally { item.request = null; }
        if (!disposed) updateSummary();
      }
    } finally { busy = false; if (!disposed) { updateSummary(); schedulePoll(); } }
  }
  const beforeUnload = (event: BeforeUnloadEvent) => { if (busy) { event.preventDefault(); event.returnValue = ''; } };
  window.addEventListener('beforeunload', beforeUnload);
  panel.addEventListener('panel-dispose', () => {
    disposed = true;
    if (poll != null) clearTimeout(poll);
    window.removeEventListener('beforeunload', beforeUnload);
    for (const item of selections) { item.check?.abort(); item.request?.abort(); releasePreview(item); }
  }, { once: true });
  updateSummary();
  return panel;
}
